/**
 * Derives the card greeting line from relationship + occasion + contact name.
 *
 * Priority rules:
 *  1. Spouse/partner relationship always wins — even on Mother's/Father's Day
 *  2. Mom/Dad relationship wins for all their occasions
 *  3. mothers_day/fathers_day occasion used as fallback ONLY when no relationship is set
 *     (prevents "Dear Mom," on a friend's Mother's Day card)
 *  4. Everyone else gets "Dear [FirstName],"
 */
export function deriveGreeting(
  relationshipType: string | null | undefined,
  occasionType:     string | null | undefined,
  contactName:      string | null | undefined,
): string {
  const rel = relationshipType?.toLowerCase();

  // 1. Spouse/partner — always "My love," regardless of occasion
  if (rel && ['spouse', 'husband', 'wife', 'partner'].includes(rel)) return 'My love,';

  // 2. Explicit parent relationship — use family name for every occasion
  if (rel === 'mom') return 'Dear Mom,';
  if (rel === 'dad') return 'Dear Dad,';

  // 3. Holiday occasion fallback — only when NO relationship is set
  //    (avoids mislabelling a friend/sibling/coworker who has a holiday occasion)
  if (!rel && occasionType === 'mothers_day') return 'Dear Mom,';
  if (!rel && occasionType === 'fathers_day') return 'Dear Dad,';

  // 4. All other relationships (child, sibling, grandparent, best_friend,
  //    friend, coworker, other) — use first name, no assumptions
  const firstName = contactName?.split(' ')[0]?.trim();
  if (firstName) return `Dear ${firstName},`;
  return 'Dear [Name],';
}

/**
 * Returns a sample greeting for the designs page based solely on occasion tab.
 * Returns empty string for generic occasions where we can't infer the relationship.
 */
export function sampleGreetingForOccasion(occasionType: string): string {
  if (occasionType === 'mothers_day') return 'Dear Mom,';
  if (occasionType === 'fathers_day') return 'Dear Dad,';
  if (occasionType === 'valentines')  return 'My love,';
  if (occasionType === 'anniversary') return 'My love,';
  return 'Dear [Name],';
}
